import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid-pro';

type GridStore = {
  paginationModel: {
    page: number;
    pageSize: number;
  };
  scrollPosition: number;
  gridSortModel: GridSortModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  columnOrder: string[];
  manualColumnView: boolean;
  myAnimals: boolean;
  /** Animal search only: restrict to animals with at least one active sale / PT listing (`saleCount` &gt; 0). */
  animalsOnSaleOnly: boolean;
  showEBVs: boolean;
  showEBVAccuracy: boolean;
  showPercentiles: boolean;
  ebvCategoryVisibility: Record<string, boolean>;
  /** Per-EBV-column (per-trait) show/hide intent, keyed by grid field (e.g. EBV5). Persists independently of the EBV display toggles so toggling EBVs off/on doesn't lose per-trait choices. */
  ebvColumnVisibility: Record<string, boolean>;
  setMyAnimals: (show: boolean) => void;
  setAnimalsOnSaleOnly: (show: boolean) => void;
  setManualColumnView: (manual: boolean) => void;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  setColumnOrder: (order: string[]) => void;
  setGridSortModel: (model: GridSortModel) => void;
  setPaginationModel: (model: { page: number; pageSize: number }) => void;
  setScrollPosition: (position: number) => void;
  setShowEBVs: (show: boolean) => void;
  setShowEBVAccuracy: (show: boolean) => void;
  setShowPercentiles: (show: boolean) => void;
  setEbvCategoryVisibility: (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  setEbvColumnVisibility: (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  resetPagination: () => void;
  resetColumnOrder: () => void;
};

const useGridStore = create<GridStore>()(
  persist(
    devtools(
      immer((set) => ({
        paginationModel: {
          page: 0,
          pageSize: 50,
        },
        scrollPosition: 0,
        gridSortModel: [],
        columnVisibilityModel: {},
        columnOrder: [],
        manualColumnView: false,
        myAnimals: false,
        animalsOnSaleOnly: false,
        showEBVs: false,
        showEBVAccuracy: false,
        showPercentiles: false,
        ebvCategoryVisibility: {},
        ebvColumnVisibility: {},
        setMyAnimals: (show) =>
          set((_state) => ({
            myAnimals: show,
          })),
        setAnimalsOnSaleOnly: (show) =>
          set((_state) => ({
            animalsOnSaleOnly: show,
          })),
        setManualColumnView: (manual) =>
          set((_state) => ({
            manualColumnView: manual,
          })),
        setShowEBVs: (show) =>
          set((_state) => ({
            showEBVs: show,
          })),
        setShowEBVAccuracy: (show) =>
          set((_state) => ({
            showEBVAccuracy: show,
          })),
        setShowPercentiles: (show) =>
          set((_state) => ({
            showPercentiles: show,
          })),
        setEbvCategoryVisibility: (updater) =>
          set((_state) => ({
            ebvCategoryVisibility:
              typeof updater === 'function'
                ? updater(_state.ebvCategoryVisibility)
                : updater,
          })),
        setEbvColumnVisibility: (updater) =>
          set((_state) => ({
            ebvColumnVisibility:
              typeof updater === 'function'
                ? updater(_state.ebvColumnVisibility)
                : updater,
          })),
        setColumnVisibilityModel: (model) =>
          set((_state) => ({
            columnVisibilityModel: model,
          })),
        setColumnOrder: (order) =>
          set((_state) => ({
            columnOrder: order,
          })),
        setGridSortModel: (model) =>
          set((_state) => ({
            gridSortModel: model,
          })),
        setPaginationModel: (model) =>
          set((_state) => ({
            paginationModel: model,
          })),
        setScrollPosition: (position) =>
          set((_state) => ({
            scrollPosition: position,
          })),
        resetPagination: () =>
          set((_state) => ({
            paginationModel: {
              page: 0,
              pageSize: 50,
            },
          })),
        resetColumnOrder: () =>
          set((_state) => ({
            columnOrder: [],
          })),
      })),
      { name: 'GridStore' },
    ),
    {
      name: 'GridStore',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export default useGridStore;
